import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  PLATFORM_ID,
  HostListener,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LazyMediaDirective } from '../../../shared/directives/lazy-media.directive';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subscription, Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import { ChatService }          from '../../../shared/services/chat.service';
import { CallService }          from '../../../shared/services/call.service';
import { AuthStateService }     from '../../../shared/states/auth.state';
import { FriendRequestService } from '../../../shared/services/friend-request.service';
import { environment }          from '../../../environments/environment';
import {
  IConversation,
  IMessage,
  IAttachment,
  ICreateGroupPayload,
  IUserSnippet,
} from '../../../shared/models/messaging.models';
import { Modal } from '../../../components/ui/modal/modal';
import { isHeicFile, readAsPreviewDataUrl } from '../../../shared/utils/heic.util';
import { LinkPreviewComponent } from '../../../components/ui/link-preview/link-preview';
import { LinkPreviewService } from '../../../shared/services/link-preview.service';

/** One file staged in the multi-file media picker, before it's sent. */
interface ISelectedMediaItem {
  file:    File;
  preview: string | null;
  type:    'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE';
}

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    emojis: ['😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇','🙂','🙃','😉','😌',
             '😍','🥰','😘','😗','😙','😚','😋','😛','😜','🤪','😝','🤑','🤗','🤭'],
  },
  {
    label: 'Gestures',
    emojis: ['👍','👎','👊','✊','🤛','🤜','🤞','✌️','🤟','🤘','👌','🤌','🤏','👈',
             '👉','👆','👇','☝️','👋','🤚','🖐','✋','🖖','💪','🦾','🤲','👐','🙌','👏'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓',
             '💗','💖','💘','💝','💟'],
  },
];

@Component({
  selector:        'app-chat',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports:         [CommonModule, FormsModule, RouterModule, Modal, LazyMediaDirective, LinkPreviewComponent],
  templateUrl:     './chat.html',
})
export class Chat implements OnInit, OnDestroy, AfterViewChecked {

  // ── DI ──────────────────────────────────────────────────────────────────────
  readonly chatService     = inject(ChatService);
  readonly callService     = inject(CallService);
  private authState        = inject(AuthStateService);
  private friendService    = inject(FriendRequestService);
  private router           = inject(Router);
  private http             = inject(HttpClient);
  private cdr              = inject(ChangeDetectorRef);
  private platformId       = inject(PLATFORM_ID);
  private toastr           = inject(ToastrService);
  private linkPreviewService = inject(LinkPreviewService);

  // ── View refs ────────────────────────────────────────────────────────────────
  @ViewChild('messageList') messageListRef!: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput') messageInputRef!: ElementRef<HTMLTextAreaElement>;

  // ── Observables (passed directly to template via async pipe) ─────────────────
  readonly conversations$        = this.chatService.conversations$;
  readonly messages$             = this.chatService.messages$;
  readonly activeConversation$   = this.chatService.activeConversation$;
  readonly totalUnread$          = this.chatService.totalUnread$;
  readonly typingUsers$          = this.chatService.typingUsers$;
  readonly presence$             = this.chatService.presence$;
  readonly loadingConversations$ = this.chatService.loadingConversations$;
  readonly loadingMessages$      = this.chatService.loadingMessages$;
  readonly hasMoreMessages$      = this.chatService.hasMoreMessages$;

  // ── UI state ─────────────────────────────────────────────────────────────────
  activeTab: 'chats' | 'groups' | 'requests' = 'chats';

  readonly tabs: { key: 'chats' | 'groups' | 'requests'; label: string }[] = [
    { key: 'chats',    label: 'Chats'    },
    { key: 'groups',   label: 'Groups'   },
    { key: 'requests', label: 'Requests' },
  ];
  searchQuery   = '';
  messageText   = '';
  isMobileSidebarOpen = false;

  // Edit state
  editingMessageId: string | null = null;
  editText = '';

  // Reply state
  replyingTo: IMessage | null = null;

  // Context menu (right-click, and the always-visible corner chevron — same menu either way)
  contextMenuMessage: IMessage | null = null;
  contextMenuX = 0;
  contextMenuY = 0;

  // Media lightbox preview
  previewAttachment: IAttachment | null = null;

  // Emoji picker
  readonly EMOJI_CATEGORIES = EMOJI_CATEGORIES;
  showEmojiPicker  = false;
  activeEmojiTab   = 0;

  /** Mobile only — the file/GIF/emoji row collapses into this one "+" menu
   *  below `sm` so the message box has room to actually be typed into (see
   *  toggleMobileAttachMenu()). Unused at `sm` and up, where all three keep
   *  their own buttons. */
  showMobileAttachMenu = false;

  // Scroll-to-bottom button
  showScrollToBottom = false;

  // Floating date pill — shows only the day whose messages are currently
  // scrolled into view (updated in updateCurrentDateLabel()).
  currentStickyDateLabel: string | null = null;

  // Group creation modal
  readonly MAX_GROUP_NAME_LENGTH = 80;
  showGroupModal    = false;
  groupName         = '';
  groupDescription  = '';
  selectedFriends:  IUserSnippet[] = [];
  availableFriends: IUserSnippet[] = [];
  friendSearch      = '';

  // Message request panel
  showRequestPanel = false;

  // Delete/clear/leave popup — shown from the sidebar's Delete action.
  // For DMs it's a single confirm; for groups it offers Clear chat / Leave
  // group / (admins only) Delete group for everyone.
  showDeleteOptionsModal = false;
  deleteOptionsConv: IConversation | null = null;
  /** Which destructive action is asking for a second confirmation, if any. */
  deleteOptionsConfirming: 'leave' | 'delete' | 'dm' | null = null;

  // Group management panel/modal
  showGroupInfoModal   = false;
  showAddMemberModal   = false;
  groupEditName        = '';
  groupEditDescription = '';
  addMemberSearch      = '';
  addMemberResults:    IUserSnippet[] = [];

  // Group name emoji picker (separate from message-composer emoji picker)
  showGroupNameEmojiPicker = false;
  activeGroupEmojiTab      = 0;

  // Group profile image change
  groupEditImageFile:    File | null = null;
  groupEditImagePreview: string | null = null;

  // Media upload — up to MAX_MEDIA_UPLOAD_COUNT files staged at once, each
  // sent as its own message (independent progress/placeholder per file).
  readonly MAX_MEDIA_UPLOAD_COUNT = 5;
  selectedMediaItems: ISelectedMediaItem[] = [];
  mediaSelectError: string | null = null;

  // GIF picker
  showGifPicker  = false;
  gifSearchQuery = '';
  gifResults: { id: string; url: string; preview: string }[] = [];
  gifLoading     = false;
  selectedGifUrl: string | null = null;
  private readonly GIPHY_KEY = 'mpVQUxeAA36qhojmJ6nnsrC6lekAAFfR';

  // ── New Message modal (Instagram-style friend search → start DM) ────────────
  showNewMessageModal  = false;
  newMsgSearch         = '';
  newMsgResults:       IUserSnippet[] = [];
  newMsgLoading        = false;
  newMsgSelected:      IUserSnippet | null = null;
  private searchInput$ = new Subject<string>();

  // ── Chat settings (auto-show media) ─────────────────────────────────────────────
  // Global, persisted locally (per device, like most chat apps' media
  // auto-download setting): when false, images/videos/GIFs render behind a
  // "tap to view" placeholder instead of loading automatically — same idea
  // as WhatsApp's media auto-download toggle.
  showChatSettingsMenu = false;
  autoShowMedia        = true;
  private readonly AUTO_SHOW_MEDIA_KEY = 'chat:autoShowMedia';
  // Messages whose media the user has manually downloaded/viewed at least
  // once with auto-show off — keyed by message id. Persisted to
  // localStorage (per-user) so a message downloaded once never asks to be
  // downloaded again, the same way WhatsApp/Telegram remember media you've
  // already fetched — across chat re-opens, page reloads, and app restarts,
  // not just for the current session.
  private revealedMediaMessageIds = new Set<string>();
  private readonly REVEALED_MEDIA_KEY_PREFIX = 'chat:downloadedMedia:';

  private get revealedMediaStorageKey(): string {
    return `${this.REVEALED_MEDIA_KEY_PREFIX}${this.myId}`;
  }

  /** Load this user's previously-downloaded media ids from localStorage. */
  private _loadRevealedMediaIds(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const raw = localStorage.getItem(this.revealedMediaStorageKey);
      if (raw) this.revealedMediaMessageIds = new Set(JSON.parse(raw));
    } catch {
      // Corrupt/unavailable storage — fall back to an empty set, no crash.
      this.revealedMediaMessageIds = new Set();
    }
  }

  /** Persist the current set of downloaded media ids to localStorage. */
  private _persistRevealedMediaIds(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.setItem(
        this.revealedMediaStorageKey,
        JSON.stringify(Array.from(this.revealedMediaMessageIds)),
      );
    } catch {
      // Storage full/unavailable — the download still works for this
      // session, it just won't be remembered next time. Non-critical.
    }
  }

  // Scroll tracking
  private shouldScrollToBottom = true;
  private lastMessageId: string | null = null;
  // True while the view is "pinned" to the newest message — mirrors WhatsApp:
  // stay pinned while at (or sending to) the bottom; unpin the moment the
  // user scrolls up to read history, including while loadOlderMessages()
  // is fetching, so loaded history never yanks the view back down.
  private stickToBottom         = true;
  private subs                 = new Subscription();

  // ── Computed shortcuts ────────────────────────────────────────────────────────
  get myId(): string {
    const user = this.authState.getAuthSnapshot().user;
    // Backend login returns { id: string } not { _id: string }
    return user?.id ?? user?._id ?? '';
  }

  get myUser() {
    return this.authState.getAuthSnapshot().user;
  }

  openMediaPreview(att: IAttachment, event: MouseEvent): void {
    event.stopPropagation();
    this.previewAttachment = att;
    this.cdr.markForCheck();
  }

  closeMediaPreview(): void {
    this.previewAttachment = null;
    this.cdr.markForCheck();
  }

  // ── Chat settings (auto-show media) ─────────────────────────────────────────────

  toggleChatSettingsMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.showChatSettingsMenu = !this.showChatSettingsMenu;
    this.cdr.markForCheck();
  }

  closeChatSettingsMenu(): void {
    this.showChatSettingsMenu = false;
    this.cdr.markForCheck();
  }


  toggleAutoShowMedia(): void {
    this.autoShowMedia = !this.autoShowMedia;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.AUTO_SHOW_MEDIA_KEY, String(this.autoShowMedia));
    }
    this.cdr.markForCheck();
  }

  /** Whether a message's media should render now, vs. behind a "tap to view" placeholder. */
  isMediaRevealed(msg: IMessage): boolean {
    return this.autoShowMedia || this.revealedMediaMessageIds.has(msg._id);
  }

  // ── Long message truncation (Read more / Read less) ─────────────────────────

  readonly MESSAGE_TRUNCATE_LENGTH = 300;
  private expandedMessageIds = new Set<string>();

  isMessageExpanded(msg: IMessage): boolean {
    return this.expandedMessageIds.has(msg._id);
  }

  toggleMessageExpanded(id: string): void {
    if (this.expandedMessageIds.has(id)) this.expandedMessageIds.delete(id);
    else this.expandedMessageIds.add(id);
    this.cdr.markForCheck();
  }

  /** Full text once expanded (or if short enough already), otherwise a truncated
   *  slice with a trailing ellipsis ahead of the Read more/Read less toggle. */
  visibleMessageText(msg: IMessage): string {
    const content = msg.content ?? '';
    if (this.isMessageExpanded(msg) || content.length <= this.MESSAGE_TRUNCATE_LENGTH) {
      return content;
    }
    return content.slice(0, this.MESSAGE_TRUNCATE_LENGTH).trimEnd() + '… ';
  }

  // Memoised per text — the template calls messageSegments() on every change
  // detection pass for every visible message.
  private segmentCache = new Map<string, { text: string; href?: string }[]>();

  /** Splits message text into plain-text and link pieces so links render as anchors. */
  messageSegments(text: string): { text: string; href?: string }[] {
    const cached = this.segmentCache.get(text);
    if (cached) return cached;

    const segments: { text: string; href?: string }[] = [];
    let cursor = 0;
    for (const url of this.linkPreviewService.findUrls(text)) {
      if (url.index > cursor) segments.push({ text: text.slice(cursor, url.index) });
      segments.push({ text: url.raw, href: url.href });
      cursor = url.index + url.raw.length;
    }
    if (cursor < text.length) segments.push({ text: text.slice(cursor) });

    if (this.segmentCache.size > 500) this.segmentCache.clear();
    this.segmentCache.set(text, segments);
    return segments;
  }

  hasLink(text: string | null | undefined): boolean {
    return !!text && this.messageSegments(text).some((seg) => !!seg.href);
  }

  /**
   * User tapped a hidden-media placeholder — download/reveal it. Remembered
   * permanently (localStorage) for this message, so it never needs to be
   * downloaded again, even after reopening the chat or reloading the app.
   */
  revealMedia(msg: IMessage, event: MouseEvent): void {
    event.stopPropagation();
    this.revealedMediaMessageIds.add(msg._id);
    this._persistRevealedMediaIds();
    this.cdr.markForCheck();
  }

  toggleEmojiPicker(e: MouseEvent): void {
    e.stopPropagation();
    this.showEmojiPicker = !this.showEmojiPicker;
    this.showGifPicker   = false;
    this.showMobileAttachMenu = false;
    this.cdr.markForCheck();
  }

  toggleMobileAttachMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.showMobileAttachMenu = !this.showMobileAttachMenu;
    this.showEmojiPicker = false;
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  pickEmoji(emoji: string): void {
    this.messageText += emoji;
    this.showEmojiPicker = false;
    this.cdr.markForCheck();
  }

  setEmojiTab(index: number): void {
    this.activeEmojiTab = index;
    this.cdr.markForCheck();
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.contextMenuMessage) this.closeContextMenu();
    if (this.showEmojiPicker)    { this.showEmojiPicker = false; this.cdr.markForCheck(); }
    if (this.showGifPicker)      { this.showGifPicker = false; this.cdr.markForCheck(); }
    if (this.showMobileAttachMenu) { this.showMobileAttachMenu = false; this.cdr.markForCheck(); }
    if (this.showGroupNameEmojiPicker) { this.showGroupNameEmojiPicker = false; this.cdr.markForCheck(); }
    if (this.showChatSettingsMenu) { this.showChatSettingsMenu = false; this.cdr.markForCheck(); }  }

  ngOnInit(): void {
    this.chatService.init();

    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(this.AUTO_SHOW_MEDIA_KEY);
      if (stored !== null) this.autoShowMedia = stored === 'true';
    }

    // Restore previously-downloaded media ids for this user so items already
    // downloaded once (with auto-show off) render immediately instead of
    // showing the "tap to download" placeholder again.
    this._loadRevealedMediaIds();

    // New-message search: debounce 300ms, load friends then filter
    this.subs.add(
      this.searchInput$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
      ).subscribe(q => this._runFriendSearch(q)),
    );

    // Trigger change detection when new messages arrive; auto-follow to the
    // bottom only for messages landing at the END of the list, and only
    // while pinned there. A change in the FIRST message's id — from
    // loadOlderMessages() prepending history after scrolling up — must
    // never trigger this, or "scroll up for more" would snap back down
    // the instant it loads.
    this.subs.add(
      this.chatService.messages$.subscribe(msgs => {
        const newLastId = msgs.length ? msgs[msgs.length - 1]!._id : null;
        if (newLastId && newLastId !== this.lastMessageId && this.stickToBottom) {
          this.shouldScrollToBottom = true;
        }
        this.lastMessageId = newLastId;
        this.cdr.markForCheck();
      }),
    );

    this.subs.add(
      this.chatService.conversations$.subscribe(() => this.cdr.markForCheck()),
    );

    this.subs.add(
      this.chatService.typingUsers$.subscribe(() => this.cdr.markForCheck()),
    );

    this.subs.add(
      this.chatService.presence$.subscribe(() => this.cdr.markForCheck()),
    );

    this.subs.add(
      this.chatService.activeConversation$.subscribe(() => this.cdr.markForCheck()),
    );

    // Surface backend-rejected edit/delete actions — e.g. the time window
    // expired since the menu was opened. The template hides those options
    // proactively (canEditMessage / canDeleteForEveryone), but this is the
    // authoritative fallback for anything that slips through. Scoped to
    // just these two events so we don't also toast benign, already-handled
    // errors like the conversation:join race (which silently retries).
    this.subs.add(
      this.chatService.socketError$.subscribe(err => {
        if (err.event === 'message:edit' || err.event === 'message:delete') {
          this.toastr.error(err.message || 'Something went wrong');
        }
      }),
    );

    // Let members know when an admin deletes a group they were in — the
    // service already removes it from the list / closes it if it was open.
    this.subs.add(
      this.chatService.groupDeleted$.subscribe(({ conversationId, groupName }) => {
        if (this.selfDeletedGroupIds.has(conversationId)) {
          this.selfDeletedGroupIds.delete(conversationId);
          return;
        }
        this.toastr.info(`"${groupName ?? 'This group'}" was deleted by an admin`);
      }),
    );
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.chatService.closeConversation();
  }

  /**
   * Bound to (load)/(loadedmetadata) on images/videos/GIFs inside message
   * bubbles. Those finish fetching asynchronously, AFTER scrollToBottom()
   * already ran and captured scrollHeight — so the container keeps growing
   * for a moment after we "landed" at the bottom, leaving the final resting
   * position short of the real bottom. In a media-heavy conversation that's
   * exactly what makes a freshly-opened chat look like it's stuck at the
   * beginning instead of the latest messages. Re-stick only while pinned,
   * so it never fights someone who's deliberately scrolled up.
   */
  onMediaLoaded(): void {
    if (this.stickToBottom) {
      requestAnimationFrame(() => this.scrollToBottom());
    }
  }

  // ── Scroll ────────────────────────────────────────────────────────────────────

  scrollToBottom(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const el = this.messageListRef?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
    this.updateCurrentDateLabel();
  }

  /**
   * Recomputes which day's floating pill should be shown, based on which
   * `[data-date-divider]` anchor has most recently scrolled past the top of
   * the message list. Anchors are in document order (oldest first), so the
   * last one that's crossed the top edge is the "current" day — matching the
   * single-floating-header behaviour of WhatsApp/Telegram/iMessage instead of
   * every day's pill trying to stick at once.
   */
  private updateCurrentDateLabel(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = this.messageListRef?.nativeElement;
    if (!el) return;
    const containerTop = el.getBoundingClientRect().top;
    const anchors = el.querySelectorAll<HTMLElement>('[data-date-divider]');
    let current: string | null = null;
    for (const anchor of Array.from(anchors)) {
      if (anchor.getBoundingClientRect().top <= containerTop + 4) {
        current = anchor.getAttribute('data-date-divider');
      } else {
        break;
      }
    }
    if (!current && anchors.length > 0) {
      current = anchors[0].getAttribute('data-date-divider');
    }
    if (current !== this.currentStickyDateLabel) {
      this.currentStickyDateLabel = current;
      this.cdr.markForCheck();
    }
  }

  scrollToMessage(messageId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = document.getElementById(`msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash highlight
      el.classList.add('ring-2', 'ring-brand-400', 'ring-offset-1');
      setTimeout(() => el.classList.remove('ring-2', 'ring-brand-400', 'ring-offset-1'), 1200);
      return;
    }

    // Not currently rendered — find out why instead of doing nothing. Most
    // commonly: I soft-deleted this message "for me", so it's permanently
    // excluded from my message list even though the other participant could
    // still reply to it.
    const conv = this.chatService.activeConversation$.value;
    if (!conv) return;

    this.chatService.getMessageStatus(conv._id, messageId).subscribe({
      next: (res) => {
        switch (res.data?.status) {
          case 'deleted_for_viewer':
            this.toastr.info('You already deleted this message');
            break;
          case 'deleted_for_everyone':
            this.toastr.info('This message was deleted');
            break;
          case 'available':
            this.toastr.info('That message is further up — scroll up to find it');
            break;
          default:
            this.toastr.error('Message not found');
        }
      },
      error: () => this.toastr.error('Message not found'),
    });
  }

  onMessageListScroll(event: Event): void {
    const el = event.target as HTMLDivElement;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    // Load older messages when scrolled near the top. Unpin from the bottom
    // while doing so — prepending history changes scrollHeight too, and
    // without this the view would snap back down the instant it renders.
    if (el.scrollTop < 80 && this.chatService.hasMoreMessages$.value) {
      this.stickToBottom = false;
      this.chatService.loadOlderMessages();
    } else {
      this.stickToBottom = distFromBottom <= 200;
    }

    // Show scroll-to-bottom button when scrolled up more than 200px from bottom
    const shouldShow = distFromBottom > 200;
    if (shouldShow !== this.showScrollToBottom) {
      this.showScrollToBottom = shouldShow;
      this.cdr.markForCheck();
    }

    this.updateCurrentDateLabel();
  }

  scrollToLatest(): void {
    this.stickToBottom = true;
    this.scrollToBottom();
    this.showScrollToBottom = false;
    this.cdr.markForCheck();
  }

  // ── Conversation selection ────────────────────────────────────────────────────

  selectConversation(conv: IConversation): void {
    this.chatService.openConversation(conv);
    this.shouldScrollToBottom = true;
    this.stickToBottom        = true;
    this.isMobileSidebarOpen  = false;
    this.replyingTo           = null;
    this.editingMessageId     = null;
    this.messageText          = '';
    this.cdr.markForCheck();
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────────

  setTab(tab: 'chats' | 'groups' | 'requests'): void {
    this.activeTab = tab;
    // Filtering is now purely client-side — no server reload needed.
    // getFilteredConversations() filters by activeTab from the shared conversations$ list.
    this.cdr.markForCheck();
  }

  /**
   * True when a conversation belongs to the given tab. Shared by
   * getTabUnreadCount() and getFilteredConversations() so the two never
   * drift out of sync with each other.
   */
  private matchesTab(conv: IConversation, tab: 'chats' | 'groups' | 'requests'): boolean {
    if (tab === 'chats') {
      // Friends' DMs — messageRequest is null, or has been ACCEPTED
      return conv.type === 'DM' && (
        !conv.messageRequest ||
        conv.messageRequest.status === 'ACCEPTED'
      );
    }
    if (tab === 'groups') {
      return conv.type === 'GROUP';
    }
    if (tab === 'requests') {
      // Only incoming pending requests (not ones I sent)
      return (
        conv.type === 'DM' &&
        conv.messageRequest?.status === 'PENDING' &&
        conv.messageRequest?.requestedBy !== this.myId
      );
    }
    return false;
  }

  /**
   * Sum of unread MESSAGE counts across conversations belonging to a given
   * tab (ignores the active search query) — used to render the badge beside
   * each tab title (Chats / Groups / Requests).
   *
   * NOTE: this previously returned conv list LENGTH (i.e. "how many chats
   * exist in this tab"), not unread count. That number barely ever changes
   * once you have a few conversations, which is why the badge looked like
   * it wasn't "updating" — it was showing the wrong thing entirely, not a
   * stale one.
   */
  getTabUnreadCount(tab: 'chats' | 'groups' | 'requests'): number {
    const conversations = this.chatService.conversations$.value;
    return conversations
      .filter(conv => this.matchesTab(conv, tab))
      .reduce((sum, conv) => sum + (conv.unreadCount ?? 0), 0);
  }

  // ── Filtering ─────────────────────────────────────────────────────────────────

  getFilteredConversations(conversations: IConversation[]): IConversation[] {
    // ── Step 1: filter by active tab ─────────────────────────────────────────
    let list = conversations.filter(conv => this.matchesTab(conv, this.activeTab));

    // ── Step 2: apply search query ────────────────────────────────────────────
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) return list;

    return list.filter(conv => {
      if (conv.type === 'GROUP') {
        return conv.groupName?.toLowerCase().includes(q);
      }
      const other = this.getOtherParticipant(conv);
      return (
        other?.displayName.toLowerCase().includes(q) ||
        other?.userName.toLowerCase().includes(q)
      );
    });
  }

  // ── Participant helpers ───────────────────────────────────────────────────────

  getOtherParticipant(conv: IConversation): IUserSnippet | null {
    return conv.participants.find(p => p._id !== this.myId) ?? null;
  }

  getConversationName(conv: IConversation): string {
    if (conv.type === 'GROUP') return conv.groupName ?? 'Group';
    return this.getOtherParticipant(conv)?.displayName ?? 'Unknown';
  }

  getConversationAvatar(conv: IConversation): string {
    if (conv.type === 'GROUP') return conv.groupImageUrl ?? '/images/group-default.png';
    const other = this.getOtherParticipant(conv);
    return other?.profileImage?.urlSmall ?? '/images/user/default.png';
  }

  isOnline(conv: IConversation): boolean {
    if (conv.type === 'GROUP') return false;
    const other = this.getOtherParticipant(conv);
    return other ? this.chatService.isOnline(other._id) : false;
  }

  // ── Message input ─────────────────────────────────────────────────────────────

  onInput(event: Event): void {
    const conv = this.chatService.activeConversation$.value;
    if (conv) this.chatService.startTyping(conv._id);

    // Fallback for keyboards where Enter never reaches onKeyDown as a
    // proper 'Enter' key — notably Android's on-screen keyboards (Gboard,
    // Samsung Keyboard, etc.), which route Enter/Send through an InputEvent
    // with inputType 'insertLineBreak' instead of a recognizable keydown.
    // Without this, pressing the keyboard's Enter/Send key on those devices
    // just inserts a newline and does nothing else — it looks like "Enter
    // doesn't send" even though the desktop keydown handler is correct.
    const inputType = (event as InputEvent).inputType;
    if (inputType === 'insertLineBreak') {
      this.messageText = this.messageText.replace(/\n$/, '');
      this.sendMessage();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    const conv = this.chatService.activeConversation$.value;
    if (!conv) return;

    // If a GIF is selected, send it first then proceed with text if any
    if (this.selectedGifUrl) {
      this.sendGif();
      // If there's also text, fall through to send it too
      if (!this.messageText.trim()) return;
    }

    const text = this.messageText.trim();

    // If we're in edit mode
    if (this.editingMessageId) {
      if (!text) return;
      this.chatService.editMessage(conv._id, this.editingMessageId, text);
      this.cancelEdit();
      return;
    }

    if (!text) return;

    this.chatService.sendMessage({
      conversationId: conv._id,
      type:           'TEXT',
      content:        text,
      replyToId:      this.replyingTo?._id,
    });

    this.messageText = '';
    this.replyingTo  = null;
    this.chatService.stopTyping(conv._id);
    this.shouldScrollToBottom = true;
    this.stickToBottom        = true;
    this.cdr.markForCheck();
  }

  // ── Reply ─────────────────────────────────────────────────────────────────────

  setReply(message: IMessage): void {
    this.replyingTo = message;
    this.closeContextMenu();
    this.messageInputRef?.nativeElement.focus();
  }

  cancelReply(): void {
    this.replyingTo = null;
  }

  // ── Edit ──────────────────────────────────────────────────────────────────────

  startEdit(message: IMessage): void {
    this.editingMessageId = message._id;
    this.editText         = message.content ?? '';
    this.messageText      = message.content ?? '';
    this.closeContextMenu();
    this.cdr.markForCheck();
    setTimeout(() => this.messageInputRef?.nativeElement.focus(), 50);
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editText         = '';
    this.messageText      = '';
  }

  // ── Delete ────────────────────────────────────────────────────────────────────

  deleteForMe(message: IMessage): void {
    const conv = this.chatService.activeConversation$.value;
    if (!conv) return;
    this.chatService.deleteMessage(conv._id, message._id, 'me');
    this.closeContextMenu();
  }

  deleteForEveryone(message: IMessage): void {
    const conv = this.chatService.activeConversation$.value;
    if (!conv) return;
    this.chatService.deleteMessage(conv._id, message._id, 'everyone');
    this.closeContextMenu();
  }

  // ── Context menu (right-click, and the hover chevron button) ──────────────────

  openContextMenu(event: MouseEvent, message: IMessage): void {
    event.preventDefault();
    this.contextMenuMessage = message;
    this.contextMenuX       = event.clientX;
    this.contextMenuY       = event.clientY;
    this.cdr.markForCheck();
  }

  closeContextMenu(): void {
    this.contextMenuMessage = null;
  }

  isMine(message: IMessage): boolean {
    return message.sender?._id === this.myId;
  }

  // ── Edit / delete-for-everyone time windows ────────────────────────────────────
  // Mirrors MESSAGE_EDIT_WINDOW_MS / MESSAGE_DELETE_FOR_EVERYONE_WINDOW_MS in
  // the backend's messaging.service.ts. This is UI-only — it hides the
  // options once they'd be rejected, so people aren't offered an action
  // that's about to fail. The backend re-validates on every request
  // regardless, so keeping these two values in sync is a nicety, not a
  // security boundary.
  readonly EDIT_WINDOW_MS                 = 15 * 60 * 1000; // 15 minutes
  readonly DELETE_FOR_EVERYONE_WINDOW_MS  = 15 * 60 * 1000; // 15 minutes

  private withinWindow(message: IMessage, windowMs: number): boolean {
    const sentAt = new Date(message.createdAt).getTime();
    return Date.now() - sentAt <= windowMs;
  }

  canEditMessage(message: IMessage): boolean {
    return this.isMine(message)
      && !message.isDeleted
      && message.type === 'TEXT'
      && this.withinWindow(message, this.EDIT_WINDOW_MS);
  }

  canDeleteForEveryone(message: IMessage): boolean {
    return this.isMine(message)
      && !message.isDeleted
      && this.withinWindow(message, this.DELETE_FOR_EVERYONE_WINDOW_MS);
  }

  // ── Read receipts ─────────────────────────────────────────────────────────────

  getReadCount(message: IMessage): number {
    return message.readBy?.filter(r => r.userId !== this.myId).length ?? 0;
  }

  // ── Typing indicator ─────────────────────────────────────────────────────────

  getTypingText(
    typingMap: Map<string, { conversationId: string; userId: string; displayName: string }[]>,
    convId: string,
  ): string {
    const typers = typingMap.get(convId) ?? [];
    if (!typers.length) return '';
    if (typers.length === 1) return `${typers[0]!.displayName} is typing…`;
    if (typers.length === 2) return `${typers[0]!.displayName} and ${typers[1]!.displayName} are typing…`;
    return 'Several people are typing…';
  }

  // ── Message request flow ──────────────────────────────────────────────────────

  acceptRequest(conv: IConversation): void {
    this.chatService.acceptMessageRequest(conv._id).subscribe(() => {
      // After accepting, reload the full list so the conversation moves
      // from Requests tab to Chats tab (messageRequest becomes null).
      this.chatService.loadConversations();
      this.cdr.markForCheck();
    });
  }

  declineRequest(conv: IConversation): void {
    this.chatService.declineMessageRequest(conv._id).subscribe(() => {
      this.chatService.loadConversations();
      this.chatService.closeConversation();
      this.cdr.markForCheck();
    });
  }

  // ── Group creation ────────────────────────────────────────────────────────────

  openGroupModal(): void {
    this.showGroupModal   = true;
    this.groupName        = '';
    this.groupDescription = '';
    this.selectedFriends  = [];
    this.friendSearch     = '';
    this.availableFriends = [];

    // Pull the user's actual friends list — not just people they already
    // have a DM with — so every friend shows up by default and via search.
    this.friendService.getFriends().subscribe({
      next: (res: any) => {
        this.availableFriends = (res?.data ?? []).map((f: any) => ({
          _id:          f._id,
          displayName:  f.displayName,
          userName:     f.userName,
          profileImage: f.profileImage ?? null,
        }));
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });

    this.cdr.markForCheck();
  }

  closeGroupModal(): void {
    this.showGroupModal = false;
  }

  get filteredAvailableFriends(): IUserSnippet[] {
    const q = this.friendSearch.toLowerCase();
    if (!q) return this.availableFriends;
    return this.availableFriends.filter(
      f => f.displayName.toLowerCase().includes(q) || f.userName.toLowerCase().includes(q),
    );
  }

  toggleFriendSelection(friend: IUserSnippet): void {
    const idx = this.selectedFriends.findIndex(f => f._id === friend._id);
    if (idx === -1) {
      this.selectedFriends = [...this.selectedFriends, friend];
    } else {
      this.selectedFriends = this.selectedFriends.filter(f => f._id !== friend._id);
    }
  }

  isFriendSelected(friend: IUserSnippet): boolean {
    return this.selectedFriends.some(f => f._id === friend._id);
  }

  createGroup(): void {
    if (!this.groupName.trim() || this.selectedFriends.length < 1) return;

    const payload: ICreateGroupPayload = {
      groupName:        this.groupName.trim(),
      memberIds:        this.selectedFriends.map(f => f._id),
      groupDescription: this.groupDescription.trim() || undefined,
    };

    this.chatService.createGroup(payload).subscribe(res => {
      if (res.success) {
        this.closeGroupModal();
        // Switch to Groups tab and open the new group
        this.activeTab = 'groups';
        this.chatService.openConversation(res.data);
        this.cdr.markForCheck();
      }
    });
  }

  // ── Calls ─────────────────────────────────────────────────────────────────

  startCall(conv: IConversation, callType: 'VOICE' | 'VIDEO'): void {
    console.log('[Chat] startCall', callType, 'myId=', this.myId,
      'participants=', conv.participants.map(p => ({ _id: p._id, name: p.displayName })));

    if (!conv.participants || conv.participants.length === 0) {
      console.error('[Chat] No participants on conversation — cannot call');
      return;
    }

    const other = this.getOtherParticipant(conv);
    if (!other) {
      console.error('[Chat] Cannot identify remote user. myId=', this.myId,
        'participants=', conv.participants.map(p => p._id));
      return;
    }

    this.callService.initiateCall(conv, callType);
  }

  // ── New Message modal ─────────────────────────────────────────────────────

  openNewMessageModal(): void {
    this.showNewMessageModal = true;
    this.newMsgSearch        = '';
    this.newMsgSelected      = null;
    this.newMsgResults       = [];
    // Pre-load full friends list on open
    this._runFriendSearch('');
    this.cdr.markForCheck();
  }

  closeNewMessageModal(): void {
    this.showNewMessageModal = false;
    this.newMsgSearch        = '';
    this.newMsgSelected      = null;
    this.newMsgResults       = [];
  }

  onNewMsgInput(): void {
    this.searchInput$.next(this.newMsgSearch);
  }

  selectNewMsgUser(user: IUserSnippet): void {
    this.newMsgSelected = user;
    this.cdr.markForCheck();
  }

  /** Open or create a DM then close the modal */
  startNewDm(): void {
    if (!this.newMsgSelected) return;

    this.chatService.openDm(this.newMsgSelected._id).subscribe({
      next: res => {
        if (res.success) {
          // Switch to the Chats tab so the new DM is visible
          this.activeTab = 'chats';

          // openConversation will add it to the list if not already there,
          // set it as active, join the socket room, and load messages.
          this.chatService.openConversation(res.data);

          this.closeNewMessageModal();
          this.cdr.markForCheck();
        }
      },
      error: () => this.closeNewMessageModal(),
    });
  }

  private _runFriendSearch(query: string): void {
    this.newMsgLoading = true;
    this.cdr.markForCheck();

    this.friendService.getFriends().subscribe({
      next: (res: any) => {
        const friends: IUserSnippet[] = (res?.data ?? []).map((f: any) => ({
          _id:          f._id,
          displayName:  f.displayName,
          userName:     f.userName,
          profileImage: f.profileImage ?? null,
        }));

        const q = query.toLowerCase().trim();
        this.newMsgResults = q
          ? friends.filter(
              f =>
                f.displayName.toLowerCase().includes(q) ||
                f.userName.toLowerCase().includes(q),
            )
          : friends;

        this.newMsgLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.newMsgLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ── Mute / delete conversation ────────────────────────────────────────────────

  // ── Group management ─────────────────────────────────────────────────────────

  isGroupAdmin(conv: IConversation): boolean {
    return (conv.groupAdmins ?? []).includes(this.myId);
  }

  isMemberDisabled(conv: IConversation, userId: string): boolean {
    return (conv.disabledMembers ?? []).includes(userId);
  }

  openGroupInfoModal(conv: IConversation): void {
    this.showGroupInfoModal = true;
    this.groupEditName = conv.groupName ?? '';
    this.groupEditDescription = conv.groupDescription ?? '';
    this.showGroupNameEmojiPicker = false;
    this.groupEditImageFile = null;
    this.groupEditImagePreview = null;
    this.cdr.markForCheck();
  }

  closeGroupInfoModal(): void {
    this.showGroupInfoModal = false;
    this.showGroupNameEmojiPicker = false;
    this.groupEditImageFile = null;
    this.groupEditImagePreview = null;
  }

  // ── Group name emoji picker ─────────────────────────────────────────────────

  toggleGroupNameEmojiPicker(e: MouseEvent): void {
    e.stopPropagation();
    this.showGroupNameEmojiPicker = !this.showGroupNameEmojiPicker;
    this.cdr.markForCheck();
  }

  setGroupEmojiTab(index: number): void {
    this.activeGroupEmojiTab = index;
    this.cdr.markForCheck();
  }

  pickGroupNameEmoji(emoji: string): void {
    // ngModel's += bypasses the input's native maxlength (that only guards
    // typing/pasting), so the cap has to be enforced here too.
    if (this.groupEditName.length >= this.MAX_GROUP_NAME_LENGTH) {
      this.toastr.info('Maximum length reached');
      return;
    }
    this.groupEditName = (this.groupEditName + emoji).slice(0, this.MAX_GROUP_NAME_LENGTH);
    this.showGroupNameEmojiPicker = false;
    this.cdr.markForCheck();
  }

  // ── Group profile image ──────────────────────────────────────────────────────

  onGroupImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;

    // Some browsers/OSes report an empty file.type for less common formats
    // like .jfif — fall back to checking the extension instead of silently
    // rejecting a genuinely valid image.
    const looksLikeImage = isHeicFile(file) || (file.type
      ? file.type.startsWith('image/')
      : /\.(jpg|jpeg|jfif|png|gif|heic|heif|webp|avif|bmp)$/i.test(file.name));
    if (!looksLikeImage) return;

    this.groupEditImageFile = file;

    const reader = new FileReader();
    reader.onload = () => {
      this.groupEditImagePreview = reader.result as string;
      this.cdr.markForCheck();
    };
    readAsPreviewDataUrl(reader, file);

    // Allow re-selecting the same file later
    input.value = '';
  }

  clearGroupImageSelection(): void {
    this.groupEditImageFile = null;
    this.groupEditImagePreview = null;
    this.cdr.markForCheck();
  }

  saveGroupName(conv: IConversation): void {
    if (!this.groupEditName.trim() && !this.groupEditImageFile) return;

    this.chatService.updateGroup(conv._id, {
      groupName:        this.groupEditName.trim() || undefined,
      groupDescription: this.groupEditDescription.trim(),
      image:            this.groupEditImageFile,
    }).subscribe({
      next: res => {
        // Optimistic update of active conversation name/description/image
        const active = this.chatService.activeConversation$.value;
        if (active && active._id === conv._id) {
          this.chatService.activeConversation$.next({
            ...active,
            groupName:        this.groupEditName.trim() || active.groupName,
            groupDescription: this.groupEditDescription.trim() || null,
            groupImageUrl:    res?.data?.groupImageUrl ?? active.groupImageUrl,
          });
        }
        this.chatService.loadConversations();
        this.closeGroupInfoModal();
        this.cdr.markForCheck();
      },
    });
  }

  openAddMemberModal(): void {
    this.showAddMemberModal = true;
    this.addMemberSearch    = '';
    this.addMemberResults   = [];

    // Same fix as openGroupModal(): candidates come from the friends list,
    // not from who the admin happens to already have a DM with.
    this.friendService.getFriends().subscribe({
      next: (res: any) => {
        this.addMemberResults = (res?.data ?? []).map((f: any) => ({
          _id:          f._id,
          displayName:  f.displayName,
          userName:     f.userName,
          profileImage: f.profileImage ?? null,
        }));
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });

    this.cdr.markForCheck();
  }

  closeAddMemberModal(): void {
    this.showAddMemberModal = false;
  }

  get filteredAddMemberResults(): IUserSnippet[] {
    const conv = this.chatService.activeConversation$.value;
    const memberIds = (conv?.participants ?? []).map(p => p._id);
    const q = this.addMemberSearch.toLowerCase();
    return this.addMemberResults
      .filter(f => !memberIds.includes(f._id))
      .filter(f => !q || f.displayName.toLowerCase().includes(q) || f.userName.toLowerCase().includes(q));
  }

  addMember(friend: IUserSnippet): void {
    const conv = this.chatService.activeConversation$.value;
    if (!conv) return;
    this.chatService.addGroupMembers(conv._id, [friend._id]).subscribe(() => {
      this.chatService.loadConversations();
      this.closeAddMemberModal();
      this.cdr.markForCheck();
    });
  }

  removeMember(conv: IConversation, userId: string): void {
    this.chatService.removeGroupMember(conv._id, userId).subscribe(() => {
      this.chatService.loadConversations();
      this.cdr.markForCheck();
    });
  }

  toggleMemberDisabled(conv: IConversation, userId: string): void {
    const disable = !this.isMemberDisabled(conv, userId);

    // Optimistic local update
    const active = this.chatService.activeConversation$.value;
    if (active && active._id === conv._id) {
      const current = active.disabledMembers ?? [];
      const newDisabled = disable
        ? [...current, userId]
        : current.filter(id => id !== userId);
      this.chatService.activeConversation$.next({ ...active, disabledMembers: newDisabled });
    }

    this.chatService.toggleMemberDisabled(conv._id, userId, disable).subscribe({
      next: () => {
        this.chatService.loadConversations();
        this.cdr.markForCheck();
      },
      error: () => {
        // Revert
        if (active && active._id === conv._id) {
          this.chatService.activeConversation$.next(active);
        }
        this.cdr.markForCheck();
      },
    });
  }

  toggleOnlyAdmins(conv: IConversation): void {
    // Optimistic update: flip the value locally immediately for instant UI feedback
    const newVal = !conv.onlyAdminsCanSend;
    const active = this.chatService.activeConversation$.value;
    if (active && active._id === conv._id) {
      this.chatService.activeConversation$.next({ ...active, onlyAdminsCanSend: newVal });
    }

    this.chatService.toggleOnlyAdminsCanSend(conv._id, newVal).subscribe({
      next: () => {
        // The socket group:updated event will apply the authoritative server value.
        // Reload conversations list to keep sidebar in sync.
        this.chatService.loadConversations();
        this.cdr.markForCheck();
      },
      error: () => {
        // Revert optimistic update on error
        if (active && active._id === conv._id) {
          this.chatService.activeConversation$.next(active);
        }
        this.cdr.markForCheck();
      },
    });
  }

  // ── Media upload ─────────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    // Reset the input so selecting the exact same file(s) again later
    // still fires a change event.
    input.value = '';
    if (!files.length) return;

    this.mediaSelectError = null;

    const availableSlots = this.MAX_MEDIA_UPLOAD_COUNT - this.selectedMediaItems.length;
    if (availableSlots <= 0) {
      this.mediaSelectError = `You can attach up to ${this.MAX_MEDIA_UPLOAD_COUNT} files at a time.`;
      this.cdr.markForCheck();
      return;
    }

    const accepted = files.slice(0, availableSlots);
    if (files.length > accepted.length) {
      this.mediaSelectError =
        `You can attach up to ${this.MAX_MEDIA_UPLOAD_COUNT} files at a time — only the first ${accepted.length} were added.`;
    }

    for (const file of accepted) {
      const mimeType = file.type;
      let type: 'IMAGE' | 'VIDEO' | 'AUDIO' | 'FILE';
      // HEIC often has an empty type outside Apple devices — still an image
      // (the API converts it to WebP).
      if (mimeType.startsWith('image/') || isHeicFile(file)) type = 'IMAGE';
      else if (mimeType.startsWith('video/')) type = 'VIDEO';
      else if (mimeType.startsWith('audio/')) type = 'AUDIO';
      else                                    type = 'FILE';

      const item: ISelectedMediaItem = { file, preview: null, type };
      this.selectedMediaItems.push(item);

      // Preview for images/videos — fill in async, item is already in the array
      if (type === 'IMAGE' || type === 'VIDEO') {
        const reader = new FileReader();
        reader.onload = (e) => {
          item.preview = e.target?.result as string;
          this.cdr.markForCheck();
        };
        readAsPreviewDataUrl(reader, file);
      }
    }

    this.cdr.markForCheck();
  }

  removeSelectedMediaItem(index: number): void {
    this.selectedMediaItems.splice(index, 1);
    this.mediaSelectError = null;
    this.cdr.markForCheck();
  }

  clearSelectedMedia(): void {
    this.selectedMediaItems = [];
    this.mediaSelectError   = null;
  }

  sendMedia(): void {
    const conv  = this.chatService.activeConversation$.value;
    const items = this.selectedMediaItems;
    if (!conv || !items.length) return;

    // Clear the picker immediately so the user can start typing again
    this.clearSelectedMedia();
    this.shouldScrollToBottom = true;
    this.stickToBottom        = true;
    this.cdr.markForCheck();

    // All selected files go up as ONE message with multiple attachments —
    // rendered as a grouped album on both sides, same as WhatsApp — rather
    // than a separate message per file.
    this.chatService.uploadMediaGroup(
      conv._id,
      // HEIC: reuse the already-decoded preview for the optimistic bubble,
      // since most browsers can't render the raw file.
      items.map(item => ({
        file: item.file,
        type: item.type,
        previewUrl: isHeicFile(item.file) ? item.preview ?? undefined : undefined,
      })),
    ).subscribe({
      next: ({ done }) => {
        // Trigger change detection on every progress tick so the % updates live
        this.cdr.markForCheck();
        if (done) {
          this.chatService.loadConversations();
          this.shouldScrollToBottom = true;
          this.stickToBottom        = true;
          this.cdr.markForCheck();
        }
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  isCanSendMessage(conv: IConversation): boolean {
    if (conv.type === 'DM') {
      if (conv.isBlocked) return false;
      // A pending/declined message request has its own gating (handled via
      // conv.messageRequest in the template) — only enforce the friendship
      // check for established conversations that never went through that
      // flow, i.e. ones that started while the two users were friends.
      if (!conv.messageRequest && conv.isFriend === false) return false;
      return true;
    }
    const isAdmin = this.isGroupAdmin(conv);
    if (conv.onlyAdminsCanSend && !isAdmin) return false;
    if (this.isMemberDisabled(conv, this.myId)) return false;
    return true;
  }

  /** "Add Friend" button shown on the not-friends banner for a DM the user
   *  can no longer message after unfriending the other participant. */
  addFriendFromChat(conv: IConversation): void {
    const other = this.getOtherParticipant(conv);
    if (!other?._id) return;

    this.friendService.send(other._id).subscribe({
      next: () => {
        this.friendService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Send friend request failed:', err),
    });
  }

  /** Navigate to a user's profile page (chat header avatar/name, group
   *  message sender avatar/name, message-request banner, etc). */
  goToUserProfile(user: IUserSnippet | null | undefined, event?: Event): void {
    if (!user?.userName) return;
    event?.stopPropagation();
    this.router.navigate(['/profile', user.userName, 'post']);
  }

  unblockConversationContact(conv: IConversation): void {
    const other = this.getOtherParticipant(conv);
    if (!other?._id) return;

    this.friendService.unblock(other._id).subscribe({
      next: () => {
        conv.isBlocked   = false;
        conv.blockedByMe = false;
        this.friendService.triggerRefresh();
        this.chatService.loadConversations();
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Unblock failed:', err),
    });
  }

  muteConversation(conv: IConversation): void {
    this.chatService.muteConversation(conv._id, !conv.isMuted).subscribe(() => {
      this.chatService.loadConversations();
      this.cdr.markForCheck();
    });
  }

  /**
   * Entry point for the sidebar's "Delete" action.
   * DMs: ask once, then clear-chat (existing behaviour, unchanged).
   * Groups: open a popup with Clear chat / Leave group / (admins only)
   * Delete group for everyone — standard chat-app pattern.
   */
  openDeleteOptionsModal(conv: IConversation): void {
    this.showDeleteOptionsModal = true;
    this.deleteOptionsConv      = conv;
    this.deleteOptionsConfirming = null;
    this.cdr.markForCheck();
  }

  closeDeleteOptionsModal(): void {
    this.showDeleteOptionsModal  = false;
    this.deleteOptionsConv       = null;
    this.deleteOptionsConfirming = null;
  }

  /** Ask "are you sure" for a destructive step before actually running it. */
  askConfirmDeleteOption(step: 'leave' | 'delete' | 'dm'): void {
    this.deleteOptionsConfirming = step;
    this.cdr.markForCheck();
  }

  cancelConfirmDeleteOption(): void {
    this.deleteOptionsConfirming = null;
    this.cdr.markForCheck();
  }

  /** Clear chat — wipes messages for me only; DM/group membership untouched. */
  clearChat(conv: IConversation): void {
    this.chatService.deleteConversation(conv._id).subscribe(() => {
      if (this.chatService.activeConversation$.value?._id === conv._id) {
        this.chatService.closeConversation();
      }
      this.chatService.loadConversations();
      this.closeDeleteOptionsModal();
      this.cdr.markForCheck();
    });
  }

  /** Leave group — removes myself as a member; the group keeps going for
   *  everyone else. If I'm the only/last admin, the backend auto-promotes
   *  another remaining member so the group never ends up admin-less. */
  leaveGroup(conv: IConversation): void {
    this.chatService.removeGroupMember(conv._id, this.myId).subscribe({
      next: () => {
        if (this.chatService.activeConversation$.value?._id === conv._id) {
          this.chatService.closeConversation();
        }
        this.chatService.loadConversations();
        this.closeDeleteOptionsModal();
        this.toastr.success('You left the group');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.toastr.error(err?.error?.message || 'Could not leave the group');
        this.cancelConfirmDeleteOption();
      },
    });
  }

  /** Deletions this client itself triggered — used to suppress the
   *  "deleted by an admin" toast from firing on your own action. */
  private selfDeletedGroupIds = new Set<string>();

  /** Delete group for everyone — admin only, permanent. */
  deleteGroupForEveryone(conv: IConversation): void {
    this.selfDeletedGroupIds.add(conv._id);
    this.chatService.deleteGroup(conv._id).subscribe({
      next: () => {
        if (this.chatService.activeConversation$.value?._id === conv._id) {
          this.chatService.closeConversation();
        }
        this.chatService.loadConversations();
        this.closeDeleteOptionsModal();
        this.toastr.success('Group deleted');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.selfDeletedGroupIds.delete(conv._id);
        this.toastr.error(err?.error?.message || 'Could not delete the group');
        this.cancelConfirmDeleteOption();
      },
    });
  }

  // ── GIF picker ───────────────────────────────────────────────────────────────

  toggleGifPicker(e: MouseEvent): void {
    e.stopPropagation();
    this.showGifPicker   = !this.showGifPicker;
    this.showEmojiPicker = false;
    this.showMobileAttachMenu = false;
    if (this.showGifPicker && this.gifResults.length === 0) {
      this.searchGifs();
    }
    this.cdr.markForCheck();
  }

  closeGifPicker(): void {
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  onGifSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Enter') this.searchGifs();
  }

  searchGifs(query?: string): void {
    const q = query ?? this.gifSearchQuery.trim();
    this.gifLoading = true;
    this.cdr.markForCheck();
    const url = !q
      ? `https://api.giphy.com/v1/gifs/trending?api_key=${this.GIPHY_KEY}&limit=20&rating=g`
      : `https://api.giphy.com/v1/gifs/search?api_key=${this.GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=20&rating=g`;
    fetch(url)
      .then(r => r.json())
      .then((data: any) => {
        this.gifResults = (data.data ?? []).map((g: any) => ({
          id:      g.id,
          url:     g.images.original.url,
          preview: g.images.fixed_height_small.url,
        }));
        this.gifLoading = false;
        this.cdr.markForCheck();
      })
      .catch(() => { this.gifLoading = false; this.cdr.markForCheck(); });
  }

  selectGif(gif: { url: string; preview: string }): void {
    this.selectedGifUrl = gif.url;
    this.showGifPicker  = false;
    this.cdr.markForCheck();
    // Focus back on input
    setTimeout(() => this.messageInputRef?.nativeElement.focus(), 50);
  }

  clearSelectedGif(): void {
    this.selectedGifUrl = null;
    this.cdr.markForCheck();
  }

  sendGif(): void {
    const conv = this.chatService.activeConversation$.value;
    if (!conv || !this.selectedGifUrl) return;
    const gifUrl = this.selectedGifUrl;
    this.clearSelectedGif();

    this.chatService.sendMessage({
      conversationId: conv._id,
      type:           'GIF',
      content:        gifUrl,
    });
    this.shouldScrollToBottom = true;
    this.stickToBottom        = true;
    this.cdr.markForCheck();
  }

  isGifUrl(text: string): boolean {
    // Giphy serves from numbered hosts (media0…media4.giphy.com) — the old
    // pattern only matched "media.giphy.com", so a quote of an older GIF
    // reply (stored as a truncated URL, losing the ".gif") showed raw text.
    return /^https?:\/\/(\S+\.gif(\?|$)|(media\d*|i)\.giphy\.com\/)/i.test(text?.trim() ?? '');
  }

  retryMessage(msg: IMessage, event?: Event): void {
    event?.stopPropagation();
    this.chatService.retryMessage(msg);
  }

  /**
   * GIF URL to show in a reply's quote, or null when the quoted message isn't
   * a GIF. Replies saved before `replyTo.gifUrl` existed only hold a
   * truncated URL snippet, so fall back to the original message if loaded.
   */
  replyGifUrl(msg: IMessage, messages: IMessage[]): string | null {
    const replyTo = msg.replyTo;
    if (!replyTo) return null;
    if (replyTo.gifUrl) return replyTo.gifUrl;
    const original = messages.find((m) => m._id === replyTo.messageId);
    if (original && !original.isDeleted && (original.type === 'GIF' || this.isGifUrl(original.content ?? ''))) {
      return original.content ?? null;
    }
    return null;
  }

  /** True when the quoted message is a GIF we can't render (old reply, original not loaded). */
  isReplySnipGif(msg: IMessage): boolean {
    return this.isGifUrl(msg.replyTo?.contentSnip ?? '');
  }

  /**
   * Preview text for a conversation's last message in the sidebar list.
   * Branches on message TYPE rather than just falling back to raw content,
   * so a GIF (content is a Giphy URL) shows "GIF" instead of the link
   * itself, and other media types get a readable label too.
   */
  getLastMessagePreview(conv: IConversation): string {
    const lm = conv.lastMessage;
    if (!lm) return 'No messages yet';

    const count = lm.attachmentCount ?? 0;

    switch (lm.type) {
      case 'GIF':   return '🎞️ GIF';
      case 'IMAGE': return count > 1 ? `📷 ${count} photos` : '📷 Photo';
      case 'VIDEO': return count > 1 ? `🎥 ${count} videos` : '🎥 Video';
      case 'AUDIO': return count > 1 ? `🎵 ${count} audio files` : '🎵 Audio';
      case 'FILE':  return count > 1 ? `📎 ${count} files` : '📎 File';
      case 'CALL':  return '📞 Call';
      default:
        // Legacy GIFs sent before the GIF type existed were stored as TEXT
        // with a Giphy URL as content — catch those too.
        if (lm.content && this.isGifUrl(lm.content)) return '🎞️ GIF';
        return lm.content || '📎 Attachment';
    }
  }

  getPresenceLabel(conv: IConversation): string {
    if (conv.type === 'GROUP') return '';
    const other = this.getOtherParticipant(conv);
    if (!other) return '';
    return this.getLastActiveLabel(other._id, other.lastSeenAt);
  }

  getLastActiveLabel(userId: string, fallbackLastSeenAt?: string | null): string {
    if (this.chatService.isOnline(userId)) return 'Online';
 
    const live = this.chatService.presence$.value[userId]?.lastSeen;
    const iso  = live ?? fallbackLastSeenAt ?? null;
    if (!iso) return 'Offline';
 
    const thenMs = new Date(iso).getTime();
    if (Number.isNaN(thenMs)) return 'Offline';
 
    const diffMs  = Math.max(0, Date.now() - thenMs);
    const diffMin = Math.floor(diffMs / 60000);
 
    if (diffMin < 1)  return 'Active just now';
    if (diffMin < 60) return `Active ${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
 
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `Active ${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
 
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `Active ${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
 
    return `Active on ${new Date(iso).toLocaleDateString()}`;
  }

  // ── Misc helpers ──────────────────────────────────────────────────────────────

  formatTime(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now  = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1)   return 'just now';
    if (mins < 60)  return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7)   return `${days}d`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  formatFullTime(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString([], {
      hour:   '2-digit',
      minute: '2-digit',
    });
  }

  // ── Date separator helpers (WhatsApp-style) ───────────────────────────────
  // Uses native `position: sticky` on each day's in-flow separator (see
  // chat.html) so the current day's pill sticks to the top of the message
  // list while scrolling — no extra JS-driven overlay pill needed.

  /** Returns true when this message should be preceded by a date separator */
  isFirstMessageOfDay(messages: IMessage[], index: number): boolean {
    if (index === 0) return true;
    return this._dayKey(messages[index].createdAt) !== this._dayKey(messages[index - 1].createdAt);
  }

  /** Human-readable date label: "Today", "Yesterday", or "12 June 2025" */
  getDateLabel(dateStr: string): string {
    return this._formatDay(new Date(dateStr));
  }

  private _dayKey(dateStr: string): string {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  private _formatDay(d: Date): string {
    const today     = new Date();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    if (this._dayKey(d.toISOString()) === this._dayKey(today.toISOString()))     return 'Today';
    if (this._dayKey(d.toISOString()) === this._dayKey(yesterday.toISOString())) return 'Yesterday';
    return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
  }

  /** Clean call content: strips leading emoji, replaces "Call ended" bare text */
  formatCallContent(content: string | undefined): string {
    if (!content) return 'Call ended';
    // Remove leading emoji (📞 or 📹) and trim
    const stripped = content.replace(/^[📞📹]\s*/, '').trim();
    // "2m 46s"  → "Call ended · 2:46"
    const mMatch = stripped.match(/(\d+)m\s*(\d+)s/);
    if (mMatch) {
      const m = mMatch[1];
      const s = mMatch[2].padStart(2, '0');
      return `Call ended · ${m}:${s}`;
    }
    // "45s" → "Call ended · 0:45"
    const sMatch = stripped.match(/^(\d+)s$/);
    if (sMatch) {
      return `Call ended · 0:${sMatch[1].padStart(2, '0')}`;
    }
    if (stripped.toLowerCase().includes('missed')) return 'Missed call';
    if (stripped.toLowerCase().includes('declined') || stripped.toLowerCase().includes('declined')) return 'Call declined';
    return stripped || 'Call ended';
  }

  trackByMessage(_: number, msg: IMessage): string { return msg._id; }
  trackByConv(_: number, conv: IConversation): string { return conv._id; }
}